import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { filter, mergeMap } from 'rxjs';

import { DocsActions } from '../docs/docs.actions';
import { selectActiveProjectId } from '../workspace/workspace.selectors';
import { DefineActions } from './define.actions';
import { compactChatMessages } from './define.models';

const DEFINE_KEYS = ['define.definition', 'define.chat', 'repos.settings'] as const;

export const loadDefine$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(DefineActions.load),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      filter(([, projectId]) => !!projectId),
      mergeMap(([, projectId]) => DEFINE_KEYS.map((key) => DocsActions.loadDoc({ projectId: projectId as string, key })))
    ),
  { functional: true }
);

export const saveDefinition$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(DefineActions.saveDefinition),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      filter(([, projectId]) => !!projectId),
      mergeMap(([{ definition }, projectId]) => [
        DocsActions.applyLocalData({ projectId: projectId as string, key: 'define.definition', data: definition }),
        DocsActions.saveDoc({ projectId: projectId as string, key: 'define.definition', data: definition }),
      ])
    ),
  { functional: true }
);

export const setChatMessages$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(DefineActions.setChatMessages),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      filter(([, projectId]) => !!projectId),
      mergeMap(([{ messages }, projectId]) => {
        const data = { messages: compactChatMessages(messages) };
        return [
          DocsActions.applyLocalData({ projectId: projectId as string, key: 'define.chat', data }),
          DocsActions.saveDoc({ projectId: projectId as string, key: 'define.chat', data }),
        ];
      })
    ),
  { functional: true }
);

export const defineEffects = { loadDefine$, saveDefinition$, setChatMessages$ };
